import React, { useRef, useState } from 'react';
import { Modal } from 'antd';
import { toast } from 'react-toastify';
import { uploadDocument } from 'services/library';
import type { Document } from 'types/library';

interface Props {
    open: boolean;
    folderId: number | null;
    onClose: () => void;
    onSuccess: (doc: Document) => void;
}

type FileStatus = 'pending' | 'uploading' | 'done' | 'error';

interface FileItem {
    id: string;
    file: File;
    thumbnail?: File;
    thumbnailPreview?: string;
    status: FileStatus;
    error?: string;
}

function formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
}

const UploadModal: React.FC<Props> = ({ open, folderId, onClose, onSuccess }) => {
    const [items, setItems] = useState<FileItem[]>([]);
    const [dragging, setDragging] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [isHandbook, setIsHandbook] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const addFiles = (fileList: FileList | null) => {
        if (!fileList) return;
        const files = Array.from(fileList);
        if (isHandbook) {
            const invalid = files.filter(f => !f.name.toLowerCase().endsWith('.pdf'));
            if (invalid.length) {
                toast.error('Cẩm nang chỉ chấp nhận file PDF');
                return;
            }
        }
        const next: FileItem[] = files.map(file => ({
            id: `${file.name}-${Date.now()}-${Math.random()}`,
            file,
            status: 'pending',
        }));
        setItems(prev => [...prev, ...next]);
    };

    const setStatus = (id: string, status: FileStatus, error?: string) =>
        setItems(prev => prev.map(f => f.id === id ? { ...f, status, error } : f));

    const pickThumbnail = (id: string, fileList: FileList | null) => {
        const picked = fileList?.[0];
        if (!picked) return;
        if (!picked.type.startsWith('image/')) {
            toast.error('Vui lòng chọn một tệp ảnh cho thumbnail');
            return;
        }
        const previewUrl = URL.createObjectURL(picked);
        setItems(prev => prev.map(f => {
            if (f.id !== id) return f;
            if (f.thumbnailPreview) URL.revokeObjectURL(f.thumbnailPreview);
            return { ...f, thumbnail: picked, thumbnailPreview: previewUrl };
        }));
    };

    const removeThumbnail = (id: string) => {
        setItems(prev => prev.map(f => {
            if (f.id !== id) return f;
            if (f.thumbnailPreview) URL.revokeObjectURL(f.thumbnailPreview);
            return { ...f, thumbnail: undefined, thumbnailPreview: undefined };
        }));
    };

    const handleClose = () => {
        if (uploading) return;
        items.forEach(f => { if (f.thumbnailPreview) URL.revokeObjectURL(f.thumbnailPreview); });
        setItems([]);
        setIsHandbook(false);
        onClose();
    };

    const handleUpload = async () => {
        const pending = items.filter(f => f.status === 'pending');
        if (!pending.length) return;
        setUploading(true);
        for (const item of pending) {
            setStatus(item.id, 'uploading');
            try {
                const doc = await uploadDocument(item.file, folderId, isHandbook, item.thumbnail);
                onSuccess(doc);
                setStatus(item.id, 'done');
            } catch (err) {
                setStatus(item.id, 'error', err instanceof Error ? err.message : 'Upload thất bại');
            }
        }
        setUploading(false);
        setItems(prev => {
            if (prev.every(f => f.status === 'done')) { handleClose(); return []; }
            return prev;
        });
    };

    const hasPending = items.some(f => f.status === 'pending');

    return (
        <Modal
            title={
                <div className="flex items-center gap-2">
                    {isHandbook && <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">Handbook</span>}
                    <span>Tải lên tài liệu</span>
                </div>
            }
            open={open}
            onCancel={handleClose}
            onOk={handleUpload}
            okText={uploading ? 'Đang tải...' : 'Tải lên'}
            cancelText="Hủy"
            okButtonProps={{ disabled: !hasPending || uploading, loading: uploading }}
            cancelButtonProps={{ disabled: uploading }}
            width={520}
        >
            <div
                className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed p-8 cursor-pointer transition-colors ${dragging ? 'border-primary bg-blue-50 dark:bg-blue-950/20' : 'border-border hover:border-primary/60'}`}
                onDrop={e => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); }}
                onDragOver={e => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onClick={() => !uploading && inputRef.current?.click()}
            >
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" className="text-text-secondary">
                    <polyline points="16 16 12 12 8 16" /><line x1="12" y1="12" x2="12" y2="21" />
                    <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
                </svg>
                <p className="text-sm text-text-secondary">Kéo thả hoặc <span className="text-primary font-medium">chọn file</span></p>
                <p className="text-xs text-text-muted">PDF, Word, Excel, Video, Ảnh</p>
                <input
                    ref={inputRef}
                    type="file"
                    multiple
                    accept={isHandbook ? '.pdf' : undefined}
                    className="hidden"
                    onChange={e => addFiles(e.target.files)}
                />
            </div>

            <label className="mt-3 flex items-center gap-2 cursor-pointer w-fit">
                <input
                    type="checkbox"
                    checked={isHandbook}
                    disabled={uploading}
                    onChange={e => {
                        setIsHandbook(e.target.checked);
                        if (e.target.checked) {
                            setItems(prev => {
                                const hasDone = prev.some(f => f.status === 'done');
                                if (hasDone) toast.warning('File đã upload không được đánh dấu là cẩm nang. Bạn cần upload lại.');
                                const invalid = prev.filter(f => !f.file.name.toLowerCase().endsWith('.pdf'));
                                if (!hasDone && invalid.length) toast.error('Đã xoá các file không phải PDF');
                                return prev
                                    .filter(f => f.status !== 'done')
                                    .filter(f => f.file.name.toLowerCase().endsWith('.pdf'));
                            });
                        }
                    }}
                    className="w-4 h-4 accent-blue-500 cursor-pointer"
                />
                <span className="text-sm text-text-primary">Đây là cẩm nang</span>
            </label>

            {items.length > 0 && (
                <ul className="mt-3 space-y-2">
                    {items.map(({ id, file, thumbnail, thumbnailPreview, status, error }) => (
                        <React.Fragment key={id}>
                            <li className="flex items-center gap-3 rounded-lg border border-border px-3 py-2 text-sm">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="shrink-0 text-text-secondary">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
                                </svg>
                                <span className="min-w-0 flex-1 truncate text-text-primary" title={error ?? file.name}>{file.name}</span>
                                <span className="shrink-0 text-xs text-text-muted">{formatSize(file.size)}</span>
                                {status === 'uploading' && <span className="shrink-0 text-xs text-primary">Đang tải...</span>}
                                {status === 'done' && <span className="shrink-0 text-xs text-success">✓</span>}
                                {status === 'error' && <span className="shrink-0 text-xs text-danger" title={error}>✗</span>}
                                {status === 'pending' && !uploading && (
                                    <button onClick={e => {
                                        e.stopPropagation();
                                        setItems(prev => {
                                            const target = prev.find(f => f.id === id);
                                            if (target?.thumbnailPreview) URL.revokeObjectURL(target.thumbnailPreview);
                                            return prev.filter(f => f.id !== id);
                                        });
                                    }}
                                        className="shrink-0 text-text-muted hover:text-danger transition-colors">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                                        </svg>
                                    </button>
                                )}
                            </li>

                            {thumbnailPreview && thumbnail ? (
                                <li className="ml-6 flex items-center gap-3 rounded-lg border border-dashed border-border px-3 py-2 text-sm">
                                    <img
                                        src={thumbnailPreview}
                                        alt={thumbnail.name}
                                        className="shrink-0 rounded-md object-cover"
                                        style={{ width: 32, aspectRatio: '3 / 4' }}
                                    />
                                    <span className="min-w-0 flex-1 truncate text-text-secondary" title={thumbnail.name}>{thumbnail.name}</span>
                                    <span className="shrink-0 text-xs text-text-muted">Thumbnail</span>
                                    {status === 'pending' && !uploading && (
                                        <button onClick={e => { e.stopPropagation(); removeThumbnail(id); }}
                                            className="shrink-0 text-text-muted hover:text-danger transition-colors">
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                                            </svg>
                                        </button>
                                    )}
                                </li>
                            ) : (
                                status === 'pending' && !uploading && (
                                    <li className="ml-6">
                                        <label className="inline-flex items-center gap-1.5 text-xs text-primary cursor-pointer hover:underline">
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="9" cy="9" r="2" /><path d="m21 15-3.1-3.1a2 2 0 0 0-2.8 0L6 21" />
                                            </svg>
                                            + Thêm thumbnail <span className="text-xs text-text-muted">(Khuyến khích tỉ lệ 3:4)</span>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                className="hidden"
                                                onChange={e => pickThumbnail(id, e.target.files)}
                                            />
                                        </label>
                                    </li>
                                )
                            )}
                        </React.Fragment>
                    ))}
                </ul>
            )}
        </Modal>
    );
};

export default UploadModal;
